import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { EventsService } from 'src/app/shared/services/data-api/events.service';
import {
  EventsContentParams,
  EventsUtilsService,
} from 'src/app/shared/utils/events-utils.service';
import {
  CollapsedEventsResponse,
  EventFactuality,
  EventTemporalityWire,
  FACTUALITY_INT_TO_ENUM,
  TEMPORALITY_INT_TO_ENUM,
} from 'src/app/shared/models/events.interface';
import { ContextualRequestService } from 'src/app/shared/services/data-api/contextual-request.service';
import { RoutingService } from 'src/app/service/routing.service';
import { ConfigService } from 'src/app/service/config.service';
import { ParamsBuilder } from 'src/app/shared/utils/params-builder';

interface V2EventsTsResponse {
  events_ts: Array<{
    date: string;
    count: number;
    sub_event: { type: string; id: string; name: string };
  }>;
}

export interface NewsChartEventRow {
  date: string;
  count: number;
  sub_event: { id: string };
}

export interface NewsChartEventsAnalytics {
  byFactuality: {
    fact: NewsChartEventRow[];
    probable: NewsChartEventRow[];
    possible: NewsChartEventRow[];
    na: NewsChartEventRow[];
  };
  byTemporality: {
    past: NewsChartEventRow[];
    present: NewsChartEventRow[];
    future: NewsChartEventRow[];
    na: NewsChartEventRow[];
  };
}

export interface NewsChartScoreFocusedEventsResult {
  events: NewsChartEventRow[];
  eventsAnalytics: NewsChartEventsAnalytics;
}

const FACT_BUCKETS: Record<
  keyof NewsChartEventsAnalytics['byFactuality'],
  EventFactuality[]
> = {
  fact: ['FACT', 'COUNTERFACT'],
  probable: ['PROBABLE', 'COUNTERPROBABLE'],
  possible: ['POSSIBLE', 'COUNTERPOSSIBLE'],
  na: ['UNKNOWN', 'NONE'],
};

const TEMP_BUCKETS: Record<
  keyof NewsChartEventsAnalytics['byTemporality'],
  EventTemporalityWire[]
> = {
  past: ['PAST'],
  present: ['PRESENT'],
  future: ['FUTURE'],
  na: ['UNKNOWN', 'NONE'],
};

@Injectable({ providedIn: 'root' })
export class NewsChartEventsService {
  constructor(
    private eventsUtils: EventsUtilsService,
    private eventsService: EventsService,
    private contextualRequest: ContextualRequestService,
    private routing: RoutingService,
    private config: ConfigService,
  ) {}

  fetchScoreFocusedEvents(
    iparams: Record<string, unknown>,
    score: string | null,
  ): Observable<NewsChartScoreFocusedEventsResult> {
    const weights$ = this.resolveEventIds(iparams, score);
    return weights$.pipe(
      mergeMap((eventIds) => this.fetchMatrix(iparams, eventIds)),
      map((response) => this.bucketise(response)),
    );
  }

  private resolveEventIds(
    iparams: Record<string, unknown>,
    score: string | null,
  ): Observable<string[] | undefined> {
    if (!score || score === 'sentiment') return of(undefined);
    return this.eventsService
      .getScoreWeights(iparams, score)
      .pipe(map((ids) => (ids && ids.length > 0 ? ids : undefined)));
  }

  private fetchMatrix(
    iparams: Record<string, unknown>,
    eventIds: string[] | undefined,
  ): Observable<CollapsedEventsResponse> {
    if (this.routing.isQuery() || this.routing.isSearch()) {
      return this.fetchMatrixQueryV2(iparams, eventIds);
    }
    const contentParams: EventsContentParams = {
      ...this.contentParamsFromIparams(iparams),
      event_ids: eventIds,
      // The chart needs all events; a clicked eventid is filtered in getEvent(), not here.
      ignoreUrlEventIds: true,
      // Keep event_id/factuality/time on the response for client-side bucketing.
      collapse_on: ['role', 'participant'],
    };
    return this.eventsUtils.fetchEventsTimeSeriesMatrix(contentParams);
  }

  // v5 has no `search_query` filter and no `events_ts` endpoint past v2.
  // Until backend adds query support to v5, query/search routes call
  // deprecated v2 `/v2/api/search_query/events_ts` and adapt the per-day
  // rows into a thin `CollapsedEventsResponse` so `bucketise()` keeps a
  // single code path. Factuality/temporality breakdown buckets stay
  // empty for query rows — v2 events_ts never produced them.
  private fetchMatrixQueryV2(
    iparams: Record<string, unknown>,
    eventIds: string[] | undefined,
  ): Observable<CollapsedEventsResponse> {
    const params = ParamsBuilder.build(
      (iparams ?? {}) as Record<string, unknown>,
      ParamsBuilder.PARAMS_FOR_ALL_REQUESTS,
      this.config.appConfig.routing.apiproj,
    );
    if (eventIds?.length) {
      params['event_ids'] = eventIds.join(',');
    }
    return this.contextualRequest
      .request('events_ts', iparams, params, 'v2')
      .pipe(
        map((resp: V2EventsTsResponse | undefined) =>
          this.adaptV2EventsTsToMatrix(resp),
        ),
      );
  }

  private adaptV2EventsTsToMatrix(
    resp: V2EventsTsResponse | undefined,
  ): CollapsedEventsResponse {
    const rows = resp?.events_ts ?? [];
    if (!rows.length) return { events: [], columns: [] };
    const columns = ['publish_time', 'event_id', 'count'];
    const events = rows.map((it) => [
      it.date,
      String(it.sub_event?.id ?? ''),
      Number(it.count) || 0,
    ]);
    return { events, columns };
  }

  private contentParamsFromIparams(
    iparams: Record<string, unknown>,
  ): EventsContentParams {
    const out: EventsContentParams = {};
    if (typeof iparams.time === 'string') out.time = iparams.time;
    if (typeof iparams.timePeriod === 'string')
      out.timePeriod = iparams.timePeriod;
    return out;
  }

  private bucketise(
    response: CollapsedEventsResponse,
  ): NewsChartScoreFocusedEventsResult {
    const emptyAnalytics = (): NewsChartEventsAnalytics => ({
      byFactuality: { fact: [], probable: [], possible: [], na: [] },
      byTemporality: { past: [], present: [], future: [], na: [] },
    });

    if (!response?.events?.length || !response?.columns?.length) {
      return { events: [], eventsAnalytics: emptyAnalytics() };
    }

    const col = (name: string) => response.columns.indexOf(name);
    const idxDate = col('publish_time');
    const idxEventId = col('event_id');
    const idxFact = col('factuality');
    const idxTemp = col('time');
    const idxCount = col('count');
    if (idxDate < 0 || idxEventId < 0 || idxCount < 0) {
      return { events: [], eventsAnalytics: emptyAnalytics() };
    }

    const toRow = (raw: (string | number)[]): NewsChartEventRow => ({
      date: String(raw[idxDate]),
      count: Number(raw[idxCount]) || 0,
      sub_event: { id: String(raw[idxEventId]) },
    });

    const result: NewsChartScoreFocusedEventsResult = {
      events: [],
      eventsAnalytics: emptyAnalytics(),
    };

    type FactKey = keyof NewsChartEventsAnalytics['byFactuality'];
    type TempKey = keyof NewsChartEventsAnalytics['byTemporality'];
    const factEntries = Object.entries(FACT_BUCKETS) as Array<
      [FactKey, EventFactuality[]]
    >;
    const tempEntries = Object.entries(TEMP_BUCKETS) as Array<
      [TempKey, EventTemporalityWire[]]
    >;

    for (const raw of response.events) {
      const row = toRow(raw);
      result.events.push(row);

      if (idxFact >= 0) {
        const fact = FACTUALITY_INT_TO_ENUM[Number(raw[idxFact])];
        if (fact) {
          for (const [bucket, enums] of factEntries) {
            if (enums.includes(fact)) {
              result.eventsAnalytics.byFactuality[bucket].push(row);
            }
          }
        }
      }

      if (idxTemp >= 0) {
        const temp = TEMPORALITY_INT_TO_ENUM[Number(raw[idxTemp])];
        if (temp) {
          for (const [bucket, enums] of tempEntries) {
            if (enums.includes(temp)) {
              result.eventsAnalytics.byTemporality[bucket].push(row);
            }
          }
        }
      }
    }

    return result;
  }
}
